import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { getUnreadCount } from "../../data/messages";
import { auth as fbAuth } from "../../firebase";

function readAuthLS() {
  try {
    return JSON.parse(localStorage.getItem("auth") || "null");
  } catch {
    return null;
  }
}

/* ---------- Icons ---------- */
function HomeIcon({ active }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z" />
      <path d="M9 21V12h6v9" />
    </svg>
  );
}

function SearchIcon({ active }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" fill={active ? "currentColor" : "none"} fillOpacity={active ? "0.15" : "0"} />
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

function CalendarIcon({ active }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" fill={active ? "currentColor" : "none"} fillOpacity={active ? "0.15" : "0"} />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ChatIcon({ active }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill={active ? "currentColor" : "none"} fillOpacity={active ? "0.15" : "0"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function ProfileIcon({ active }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" fill={active ? "currentColor" : "none"} fillOpacity={active ? "0.2" : "0"} />
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

/* ---------- Component ---------- */
export default function MobileBottomNav() {
  const { t } = useTranslation();
  const location = useLocation();

  const [auth, setAuth] = useState(readAuthLS);
  const [fbUser, setFbUser] = useState(null);
  const [fbReady, setFbReady] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const unsub = fbAuth.onAuthStateChanged((u) => {
      // Same rule as Navbar.jsx: an email/password session whose email is
      // not verified does NOT count as authenticated. Without this filter
      // the bottom nav showed the logged-in tabs (Dashboard/Messages/…)
      // while the header correctly showed "Sign in".
      const verifiedUser =
        u &&
        (!u.providerData.some((p) => p.providerId === "password") || u.emailVerified)
          ? u
          : null;
      setFbUser(verifiedUser);
      setFbReady(true);
      // No usable session -> re-read (Navbar clears stale localStorage auth).
      if (!verifiedUser) setAuth(readAuthLS());
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    setAuth(readAuthLS());
  }, [location.pathname]);

  // Re-read auth when Navbar clears stale localStorage credentials, so both
  // components flip to the guest UI at the same moment.
  useEffect(() => {
    const onCleared = () => setAuth(null);
    window.addEventListener("profio:auth-cleared", onCleared);
    return () => window.removeEventListener("profio:auth-cleared", onCleared);
  }, []);

  // Same rule as Navbar.jsx: while Firebase is still initialising, trust
  // localStorage so genuinely authed users don't get a guest-tabs flash;
  // once Firebase resolves, a real session (fbUser) is required. Keeping the
  // two components on one rule prevents the header drawer and the bottom nav
  // from showing contradictory auth states.
  const isAuthed = !!(auth?.token && (fbUser || !fbReady));

  useEffect(() => {
    if (!fbReady || !isAuthed) {
      setUnread(0);
      return;
    }
    let alive = true;
    let id = null;
    const tick = async () => {
      try {
        const n = await getUnreadCount();
        if (alive) setUnread(Number(n || 0));
      } catch {}
    };
    tick();
    id = setInterval(tick, 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [isAuthed, fbReady]);

  const path = location.pathname;

  // Hide on admin, auth, or onboarding pages
  if (
    path.startsWith("/admin") ||
    path === "/login" ||
    path === "/signup" ||
    path.startsWith("/onboarding")
  ) {
    return null;
  }

  const isTutor = (auth?.role || "").toUpperCase() === "TUTOR";

  const tabs = isAuthed
    ? [
        {
          to: "/dashboard",
          icon: (a) => <HomeIcon active={a} />,
          label: t("nav.panel", { defaultValue: "Dashboard" }),
          isActive: path === "/dashboard" || path.startsWith("/dashboard/"),
        },
        // Search is for clients looking for a specialist. A specialist has no
        // reason to search for other specialists, so this tab is hidden for
        // the TUTOR role (removing it also keeps the bar to 4 even tabs).
        ...(isTutor ? [] : [{
          to: "/search",
          icon: (a) => <SearchIcon active={a} />,
          label: t("nav.search", { defaultValue: "Пошук" }),
          isActive: path === "/search",
        }]),
        {
          to: isTutor ? "/bookings" : "/my-requests",
          icon: (a) => <CalendarIcon active={a} />,
          // Short label: "Мої бронювання"/"Мої запити" wrap onto two lines and
          // make this tab taller/asymmetric vs. its single-line neighbours.
          label: isTutor
            ? t("nav.bookings", { defaultValue: "Бронювання" })
            : t("nav.requests", { defaultValue: "Запити" }),
          isActive: path === "/bookings" || path === "/my-requests",
        },
        {
          to: "/messages",
          icon: (a) => <ChatIcon active={a} />,
          label: t("navbar.messages", { defaultValue: "Messages" }),
          isActive: path.startsWith("/messages") || path.startsWith("/chat"),
          badge: unread,
        },
        {
          to: "/account",
          icon: (a) => <ProfileIcon active={a} />,
          label: t("nav.account", { defaultValue: "Account" }),
          isActive: path.startsWith("/account"),
        },
      ]
    : [
        {
          to: "/",
          icon: (a) => <HomeIcon active={a} />,
          label: t("nav.home", { defaultValue: "Home" }),
          isActive: path === "/",
        },
        {
          to: "/search",
          icon: (a) => <SearchIcon active={a} />,
          label: t("nav.search", { defaultValue: "Пошук" }),
          isActive: path === "/search",
        },
        {
          to: "/login",
          icon: (a) => <ProfileIcon active={a} />,
          label: t("navbar.signIn"),
          isActive: path === "/login",
        },
      ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden border-t border-white/10 bg-[#272c39]/96 backdrop-blur-md"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="flex">
        {tabs.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            className={[
              "relative flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors",
              tab.isActive ? "text-[#7269e3]" : "text-white/50 active:text-white/80",
            ].join(" ")}
          >
            <div className="relative">
              {tab.icon(tab.isActive)}
              {tab.badge > 0 && (
                <span className="absolute -top-1 -right-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#7269e3] px-1 text-[9px] font-bold leading-none text-white ring-2 ring-[#272c39]">
                  {tab.badge > 9 ? "9+" : tab.badge}
                </span>
              )}
            </div>
            <span className="leading-none">{tab.label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
}
